"use client";

import { cn } from "@/lib/utils";
import { controlsColumnsDef } from "@/src/components/dataTable/controlsColumns";
import { useQuery } from "@/src/lib/tanstack/react-query-exports";
import { resIsSuccess } from "@/utils/httpStatus";
import { findObjectWithValueAtPath } from "@/utils/objectManipulation";
import { DataTable } from "@common/components/dataTables/dataTable";
import { DataTableSkeleton } from "@common/components/ui/dataTableSkeleton";
import { toast } from "@common/components/ui/sonner";
import axios from "axios";
import { useRouter } from "next/navigation";
import * as React from "react";
import { useEffect } from "react";
import ReactConfetti from "react-confetti";

import { securityControlsCategories } from "@/packages/common/src/workers/securityControls";
import { navigateToPath } from "@/utils/DOM";
import { DataTableCategories } from "@common/components/dataTables/DataTableCategories";
import type { FlattenedControl } from "@common/utils/securityControlsHelper";
import {
  type Cell,
  Column,
  ColumnDef,
  ColumnFiltersState,
  FilterFn,
  RowData,
  SortingFn,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  sortingFns,
  useReactTable,
} from "@tanstack/react-table";

export interface ControlsDataTableProps {
  isLoadingExt?: boolean;
  title?: string;
  hideDropdown?: boolean;
  menuBarItemTitles?: string[];
  currentUser: any;
  confetti?: boolean;
  controlsData?: FlattenedControl[];
  className?: string;
}

export const ControlsDataTable = React.forwardRef<HTMLDivElement, ControlsDataTableProps>(
  ({ currentUser, menuBarItemTitles, controlsData, confetti, className, ...props }, ref) => {
    const [showArchived, setShowArchived] = React.useState(true);
    const [dataFiltered, setDataFiltered] = React.useState<FlattenedControl[]>(controlsData ?? []);
    const [isLoading, setIsLoading] = React.useState(false);

    /*
    const {
      data: formsQueryResults,
      isLoading,
      isFetching,
    } = useQuery({
      queryKey: ["allFormsPrefetched"],
      queryFn: getAllForms,
      //refetchOnWindowFocus: false,
      refetchOnMount: false,
    });
    */

    //for page routing
    const router = useRouter();

    const dataArray: FlattenedControl[] = [];
    //let dataFiltered: entriesData[] = entriesState.archivedEntries.length > 0  ? entriesState.archivedEntries : [];

    const handleArchiveEntry = async (rowId: string) => {
      const payload = {
        id: rowId,
        isArchived: true,
      };
      //send to api via axios
      //console.log("payload archiving", payload);
      //remove it optimistically
      setDataFiltered(dataFiltered.filter((item) => item.id !== rowId));
      const response = await axios.post("/api/archiveEntry", payload);
      if (resIsSuccess(response.status)) {
        toast({
          title: "Form Archived",
          message: "Success",
          type: "default",
        });

        return;
      } else {
        //add it back since it failed
        setDataFiltered([...dataFiltered, dataArray.find((item) => item.id === rowId) as FlattenedControl]);
        console.error(response);
        toast({
          title: "Form Archive Failed",
          message: "Error",
          type: "error",
        });
      }
    };

    const handleApproveEntry = async (rowId: string) => {
      console.log("approving entry", rowId, dataFiltered);
      //Find the entry in the dataFiltered array
      const newDataFiltered = dataFiltered.map((entry) => {
        if (entry.id === rowId) {
          //we found it
          //saveFormStageToDB({ formId: rowId, isApproved: true, isDownloaded: undefined });
          return { ...entry, isApproved: true, status: "Approved" };
        }
        return entry;
      });
      console.log("newDataFiltered", newDataFiltered);
      setDataFiltered(newDataFiltered);
      return true;
    };

    const getRowURL = (rowId: string, rowData?: Cell<any, unknown>[]): string => {
      if (!rowData) {
        console.error("rowData not found");
        return `/entries/doc/${rowId}`;
      }
      const statusObj = findObjectWithValueAtPath(rowData, ["column", "id"], "status") as Cell<any, unknown> | undefined;
      let status = "";
      if (!statusObj) {
        console.error("statusObj not found");
        return `/entries/doc/${rowId}`;
      } else {
        status = statusObj.getValue() as string;
      }

      switch (status) {
        case "In Progress":
          return `/entries/doc/${rowId}`;
        case "Pending Approval":
          return `/entries/doc/${rowId}?tab=Approval`;
        case "Approved":
          return `/entries/doc/${rowId}?tab=Documents`;
        default:
          return `/entries/doc/${rowId}`;
      }
    };

    const handleEntriesRowClick = (rowId: string, rowData?: Cell<any, unknown>[], e?: any) => {
      debugger;
      const url = getRowURL(rowId, rowData);
      if (url) {
        navigateToPath(router, url, undefined, e);
      }
    };

    const columns = controlsColumnsDef();

    return (
      <div className={cn("", className)}>
        {!isLoading ? (
          <DataTableCategories
            columns={columns}
            data={dataFiltered}
            menuBarItemTitles={menuBarItemTitles}
            filterTags={[...securityControlsCategories]}
            //initiallyHiddenColumns={["id", "createdAt", "status", "patientEmail", "internalNotes", "privilege"]}
            //filterMenuBarsBy="category"
            //getRowURL={getRowURL}
            //rowClickHandler={handleEntriesRowClick}
            /*initialState={{
              sorting: [{ id: "updatedAt", desc: true }],
            }}*/
            hidePagination={false}
            className=""
          />
        ) : (
          <div className="min-w-full transition-all">
            <DataTableSkeleton />
          </div>
        )}
        {confetti && <ReactConfetti className="" width={window.innerWidth} height={window.innerHeight} recycle={false} wind={0.02} gravity={0.09} />}
      </div>
    );
  }
);
ControlsDataTable.displayName = "ControlsDataTable";
